import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CompletionBar from '../CompletionBar';
import MandateEndDateField from '../../property/MandateEndDateField';
import BarreEtape from '../BarreEtape';

const API = process.env.REACT_APP_BACKEND_URL;

const Step5_MandateMedia = ({ data, updateData, nextStep, prevStep }) => {
  const navigate = useNavigate();
  const [negociateurs, setNegociateurs] = useState([]);
  const [loadingNego, setLoadingNego] = useState(true);
  const [negoError, setNegoError] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);

  useEffect(() => {
    loadNegociateurs();
  }, []);

  const loadNegociateurs = async () => {
    setLoadingNego(true);
    setNegoError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setNegoError('Session expirée. Veuillez vous reconnecter.');
        setLoadingNego(false);
        return;
      }
      
      const response = await axios.get(`${API}/api/users/negotiators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Négociateurs chargés:', response.data);
      setNegociateurs(response.data || []);
      
      // Auto-sélectionner si un seul négociateur et aucun n'est encore choisi
      if (response.data?.length === 1 && !data.negociateur) {
        updateData({ negociateur: response.data[0].id });
      }
    } catch (error) {
      console.error('Erreur chargement négociateurs:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Token invalide — forcer la reconnexion
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      } else {
        setNegoError('Impossible de charger les négociateurs. Vérifiez votre connexion.');
      }
      setNegociateurs([]);
    } finally {
      setLoadingNego(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    
    try {
      // Convertir chaque fichier en base64
      const photoPromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: file.name,
              size: file.size,
              type: file.type,
              url: reader.result // base64 data URI
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const newPhotos = await Promise.all(photoPromises);
      updateData({ photos: [...data.photos, ...newPhotos] });
    } catch (error) {
      console.error('Erreur upload photos:', error);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDocUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingDocs(true);
    
    const newDocs = files.map(file => ({
      id: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type
    }));

    updateData({ documents: [...data.documents, ...newDocs] });
    setUploadingDocs(false);
  };

  const removePhoto = (photoId) => {
    updateData({ photos: data.photos.filter(p => p.id !== photoId) });
  };

  const removeDoc = (docId) => {
    updateData({ documents: data.documents.filter(d => d.id !== docId) });
  };

  const isComplete = data.negociateur;
  const hasMandat = data.numero_mandat && data.numero_mandat.trim() !== '';

  return (
    <div className="space-y-6">
      {/* Barre de complétion */}
      <CompletionBar data={data} currentStep={5} />
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Mandat & Médias
        </h2>
        <p className="text-gray-600">
          Informations du mandat et documents associés.
        </p>
      </div>

      {/* Informations Mandat */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <i className="bi bi-file-earmark-text text-blue-600"></i>
          Informations du mandat
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Numéro de mandat */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numéro de mandat
            </label>
            <input
              type="text"
              value={data.numero_mandat}
              onChange={(e) => updateData({ numero_mandat: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="MAN-2024-001"
            />
            <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1.5">
              {hasMandat ? (
                <>
                  <i className="bi bi-check-circle-fill text-green-600"></i>
                  Le bien sera actif
                </>
              ) : (
                <>
                  <i className="bi bi-exclamation-triangle-fill text-amber-500"></i>
                  Sans mandat, le bien restera en brouillon
                </>
              )}
            </p>
          </div>

          {/* Date de mandat */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de mandat
            </label>
            <input
              type="date"
              value={data.date_mandat}
              onChange={(e) => updateData({ date_mandat: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Date de fin de mandat + nudge */}
        <MandateEndDateField
          dateFin={data.date_fin_mandat || ''}
          dateDebut={data.date_mandat || ''}
          typeMandat={data.type_mandat || ''}
          onChange={(v) => updateData({ date_fin_mandat: v })}
          label="Date de fin de mandat"
          labelClassName="block text-sm font-medium text-gray-700 mb-2"
          inputClassName="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />

        {/* Négociateur */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Négociateur responsable <span className="text-red-500">*</span>
          </label>
          {negoError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{negoError}</p>
              <button
                onClick={loadNegociateurs}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Réessayer
              </button>
            </div>
          ) : loadingNego ? (
            <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm">
              Chargement des négociateurs...
            </div>
          ) : (
            <select
              value={data.negociateur}
              onChange={(e) => {
                updateData({ negociateur: e.target.value });
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Sélectionner un négociateur --</option>
              {negociateurs.map((neg) => (
                <option key={neg.id} value={neg.id}>
                  {neg.prenom} {neg.nom}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Statut Badge */}
        <div className="bg-white rounded-lg p-4 border-2 border-dashed">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">Statut du bien :</span>
            </div>
            <div>
              {hasMandat ? (
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  ACTIF
                </span>
              ) : (
                <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  BROUILLON
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Photos */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <i className="bi bi-images text-slate-600"></i>
          Photos (optionnel)
        </h3>
        
        <div className="space-y-4">
          {/* Zone d'upload */}
          <label className="block">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors">
              <i className="bi bi-cloud-upload text-4xl text-gray-400 mb-2"></i>
              <p className="text-sm text-gray-600">Cliquez ou glissez des images ici</p>
              <p className="text-xs text-gray-500 mt-1">JPG, PNG - Max 10 Mo par fichier</p>
            </div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </label>

          {/* Grille des photos */}
          {data.photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.url}
                    alt={photo.name}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => removePhoto(photo.id)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i className="bi bi-x text-sm"></i>
                  </button>
                  <div className="text-xs text-gray-600 mt-1 truncate">{photo.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Documents */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
          <i className="bi bi-file-earmark-pdf text-red-600"></i>
          Documents (optionnel)
        </h3>
        
        <div className="space-y-4">
          {/* Zone d'upload */}
          <label className="block">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors">
              <i className="bi bi-file-earmark-arrow-up text-4xl text-gray-400 mb-2"></i>
              <p className="text-sm text-gray-600">Cliquez pour ajouter des documents</p>
              <p className="text-xs text-gray-500 mt-1">PDF, Word, Excel - Max 20 Mo par fichier</p>
            </div>
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleDocUpload}
              className="hidden"
            />
          </label>

          {/* Liste des documents */}
          {data.documents.length > 0 && (
            <div className="space-y-2">
              {data.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-center gap-3">
                    <i className="bi bi-file-earmark-pdf text-red-600 text-xl"></i>
                    <div>
                      <div className="text-sm font-medium">{doc.name}</div>
                      <div className="text-xs text-gray-500">
                        {(doc.size / 1024 / 1024).toFixed(2)} Mo
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeDoc(doc.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Validation */}
      {!isComplete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <i className="bi bi-exclamation-triangle text-yellow-600"></i>
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">Champs obligatoires manquants</p>
              <p>Veuillez renseigner le négociateur responsable.</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <BarreEtape classeOrdinateur="md:pt-6 md:border-t">
        <button
          onClick={prevStep}
          className="h-11 md:h-auto px-4 md:px-6 md:py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors flex items-center gap-2"
        >
          <i className="bi bi-arrow-left"></i>
          Retour
        </button>
        <button
          onClick={nextStep}
          disabled={!isComplete}
          className="h-12 md:h-auto px-6 md:py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
        >
          Suivant
          <i className="bi bi-arrow-right"></i>
        </button>
      </BarreEtape>
    </div>
  );
};

export default Step5_MandateMedia;
